import { createContext, useContext, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const FarmaciaChromeContext = createContext(null);

const PAGE_CHROME = {
  "/farmacia/strutture": {
    title: "Strutture collegate",
    subtitle: "Gestisci strutture, referenti, volumi operativi e stato del servizio.",
  },
  "/farmacia/ospiti": {
    title: "Ospiti",
    subtitle: "Gestisci assistiti delle strutture collegate e clienti diretti della farmacia.",
  },
  "/farmacia/pratiche": {
    title: "Pratiche",
    subtitle: "Gestisci Carepy, terapie da visionare, ricette, documenti richiesti e pratiche completate.",
  },
  "/farmacia/magazzino": {
    title: "Magazzino",
    subtitle:
      "Catalogo prodotti, scorte multi-fonte per ospite e alert operativi. Non è un magazzino fisico completo (niente lotti/scadenze di giacenza).",
  },
  "/farmacia/ordini": {
    title: "Ordini",
    subtitle: "Gestisci ordini da strutture e clienti diretti, conferme, evasione parziale e stato consegna.",
  },
  "/farmacia/amministrazione": {
    title: "Amministrazione",
    subtitle: "Gestisci documenti contrattuali, fatture e riepiloghi extra per ospite.",
  },
  "/farmacia/report": {
    title: "Report",
    subtitle: "Monitora andamento operativo, consegne, ordini, assistiti e risultati economici.",
  },
  "/farmacia/richieste": {
    title: "Richieste Carepy",
    subtitle:
      "Carica uno o più PDF Carepy: associazione per codice fiscale, conferma se incerta, gestione duplicati e file illeggibili.",
  },
  "/farmacia/controllo-ricette": {
    title: "Pratiche — verifica ricette",
    subtitle:
      "Collegamento compatibile: per le nuove attività usa Pratiche → Verifica ricette.",
  },
  "/farmacia/scorte": {
    title: "Scorte",
    subtitle:
      "Multi-fonte per ospite/prodotto: Carepy, dichiarato struttura, verificato farmacia, ordinato e consegnato.",
  },
  "/farmacia/consegne": {
    title: "Consegne",
    subtitle: "Gestisci e monitora tutte le consegne pianificate e completate.",
  },
  "/farmacia/autista": {
    title: "Vista autista",
    subtitle: "Solo informazioni logistiche per le consegne del giorno. Nessun dato sanitario.",
    backTo: "/farmacia/consegne",
    backLabel: "Consegne",
  },
  "/farmacia/fatturazione": {
    title: "Fatturazione",
    subtitle:
      "Carica fatture PDF per struttura con metadati e stati. Un PDF unico con più fatture viene scorporato automaticamente.",
  },
  "/farmacia/clienti": {
    title: "Clienti diretti",
    subtitle: "Anagrafica pazienti privati della farmacia, terapie e invio al medico.",
  },
  "/farmacia/catalogo": {
    title: "Catalogo prodotti",
    subtitle: "Importa Excel/CSV (codice interno, MINSAN, AIC, nome). Ricerca backend paginata.",
  },
  "/farmacia/impostazioni": {
    title: "Impostazioni",
    subtitle: "Configura il sistema in base alle esigenze della tua farmacia.",
  },
};

export function getFarmaciaRouteChrome(pathname, search = "") {
  const path = (pathname || "/").replace(/\/+$/, "") || "/";
  const vista = new URLSearchParams(search).get("vista");

  if (path === "/farmacia") return { dashboard: true };

  if (path === "/farmacia/pratiche" && vista === "copertura") {
    return {
      title: "Pratiche — controllo copertura",
      subtitle:
        "Visualizza e scarica le ricette della struttura, conferma la copertura per farmaco, segnala equivalenti o documenti illeggibili.",
    };
  }

  if (PAGE_CHROME[path]) return PAGE_CHROME[path];

  if (path.startsWith("/farmacia/ospite/")) {
    return {
      title: "Scheda paziente",
      subtitle: "Anagrafica, terapia, scorte, pratiche e documenti.",
      backTo: "/farmacia/ospiti",
      backLabel: "Assistiti",
    };
  }

  if (path.startsWith("/farmacia/struttura/")) {
    return {
      title: "Struttura",
      subtitle: "Panoramica ospiti aderenti al deblistering e stato pratiche ricette",
      backTo: "/farmacia",
      backLabel: "Tutte le strutture",
    };
  }

  return { title: "Portale Farmacia", subtitle: "" };
}

export function FarmaciaChromeProvider({ children }) {
  const [pageChrome, setPageChrome] = useState(null);
  const [actionsNode, setActionsNode] = useState(null);
  const value = useMemo(
    () => ({ pageChrome, setPageChrome, actionsNode, setActionsNode }),
    [pageChrome, actionsNode]
  );
  return <FarmaciaChromeContext.Provider value={value}>{children}</FarmaciaChromeContext.Provider>;
}

export function FarmaciaPageChrome({ title, subtitle, backTo, backLabel, children }) {
  const ctx = useContext(FarmaciaChromeContext);
  const setPageChrome = ctx?.setPageChrome;
  const actionsNode = ctx?.actionsNode;

  useLayoutEffect(() => {
    if (!setPageChrome) return undefined;
    const next = {};
    if (title !== undefined) next.title = title;
    if (subtitle !== undefined) next.subtitle = subtitle;
    if (backTo !== undefined) next.backTo = backTo;
    if (backLabel !== undefined) next.backLabel = backLabel;
    setPageChrome(next);
    return () => setPageChrome(null);
  }, [setPageChrome, title, subtitle, backTo, backLabel]);

  if (!children || !actionsNode) return null;
  return createPortal(
    <div className="flex flex-wrap items-center justify-end gap-2">{children}</div>,
    actionsNode
  );
}

export function FarmaciaHeaderTitle({ greeting }) {
  const location = useLocation();
  const ctx = useContext(FarmaciaChromeContext);
  const fallback = getFarmaciaRouteChrome(location.pathname, location.search);
  const override = ctx?.pageChrome;
  const isDashboard = Boolean(fallback.dashboard);
  const merged = { ...fallback, ...(override || {}) };

  if (isDashboard) return greeting || null;

  const { title, subtitle, backTo, backLabel } = merged;

  return (
    <div className="min-w-0">
      {backTo ? (
        <Link
          to={backTo}
          className="mb-0.5 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-emerald-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {backLabel || "Indietro"}
        </Link>
      ) : null}
      <p className="truncate text-xl font-semibold leading-tight tracking-tight text-slate-900 md:text-2xl">
        {title}
      </p>
      {subtitle ? (
        <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">{subtitle}</p>
      ) : null}
    </div>
  );
}

export function FarmaciaHeaderActions() {
  const location = useLocation();
  const ctx = useContext(FarmaciaChromeContext);
  if (!ctx || getFarmaciaRouteChrome(location.pathname, location.search).dashboard) return null;
  return <div ref={ctx.setActionsNode} className="flex shrink-0 flex-wrap items-center justify-end gap-2" />;
}
